'use client';

import React, { useState } from 'react';
import { Users, Eye, EyeOff } from 'lucide-react';

interface Vote {
  id: string;
  participantName: string;
  choice: 'boy' | 'girl';
  timestamp: string;
}

interface ParticipantGridProps {
  votes: Vote[];
  revealed: boolean;
}

export default function ParticipantGrid({ votes, revealed }: ParticipantGridProps) {
  const [showAll, setShowAll] = useState(false);
  const displayVotes = showAll ? votes : votes.slice(0, 8);

  return (
    <div className="bg-card rounded-2xl border border-border card-shadow p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users size={15} className="text-muted-foreground" />
          <h3 className="text-sm font-700 text-foreground">Participantes</h3>
        </div>
        <span className="text-xs font-600 text-muted-foreground bg-muted rounded-full px-2 py-0.5 font-tabular">
          {votes.length}
        </span>
      </div>

      {votes.length === 0 ? (
        <div className="text-center py-6">
          <span className="text-3xl mb-2 block">🗳️</span>
          <p className="text-sm text-muted-foreground">Aún no hay votos</p>
          <p className="text-xs text-muted-foreground mt-1">
            Comparte el enlace para que participen
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-1.5">
            {displayVotes.map((vote) => (
              <div
                key={`participant-${vote.id}`}
                className="flex items-center gap-2.5 py-1.5 px-2 rounded-xl hover:bg-muted/40 transition-colors group"
              >
                {/* Avatar */}
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-700 text-white flex-shrink-0 ${
                    vote.choice === 'boy' ? 'bg-boy-gradient' : 'bg-girl-gradient'
                  }`}
                >
                  {vote.participantName.charAt(0).toUpperCase()}
                </div>

                {/* Name */}
                <span className="flex-1 text-sm font-500 text-foreground truncate">
                  {vote.participantName}
                </span>

                {/* Choice */}
                {revealed ? (
                  <span
                    className={`text-xs font-600 px-2 py-0.5 rounded-full flex-shrink-0 ${
                      vote.choice === 'boy' ?'bg-blue-100 text-blue-500' :'bg-pink-100 text-pink-500'
                    }`}
                  >
                    {vote.choice === 'boy' ? '💙 Niño' : '🩷 Niña'}
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground flex items-center gap-1 flex-shrink-0">
                    <EyeOff size={10} />
                    Oculto
                  </span>
                )}
              </div>
            ))}
          </div>

          {votes.length > 8 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="w-full mt-3 py-2 rounded-xl border border-border text-xs font-600 text-muted-foreground hover:bg-muted transition-all flex items-center justify-center gap-1.5"
            >
              {showAll ? (
                <>
                  <EyeOff size={12} />
                  Mostrar menos
                </>
              ) : (
                <>
                  <Eye size={12} />
                  Ver {votes.length - 8} más
                </>
              )}
            </button>
          )}
        </>
      )}
    </div>
  );
}